import React from 'react';
import {
    Combobox,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxItemWithDescription,
    ComboboxList,
    ComboboxOptions,
    ComboboxSearch,
    ComboboxTrigger,
} from '@schuettflix/planum-react';
import { useTranslation } from 'react-i18next';
import { ComboboxOption } from './types.ts';
import { cn } from '@/shared/utils/cn.ts';

export type { ComboboxOption as SearchableComboboxOption };

export type SearchableComboboxProps = {
    label?: string;
    placeholder?: string;
    size?: 'md' | 'lg';
    error?: string;
    search: string;
    onSearchChange: (value: string) => void;
    loading?: boolean;
    options: ComboboxOption[];
    value: string | undefined;
    onValueChange: (value: string) => void;
    disabled?: boolean;
    dataTest?: string;
    className?: string;
    // ComboboxTrigger doesn't forward a ref, so this wrapper is what RHF's shouldFocusError can focus to scroll to it.
    fieldRef?: React.Ref<HTMLDivElement>;
};

export function SearchableCombobox({
    label,
    placeholder,
    size = 'lg',
    error,
    search,
    onSearchChange,
    loading,
    options,
    value,
    onValueChange,
    disabled,
    dataTest,
    className,
    fieldRef,
}: SearchableComboboxProps) {
    const { t } = useTranslation();

    return (
        <Combobox<ComboboxOption>
            value={value}
            onValueChange={newValue => {
                // Prevent toggle-deselect: clicking the already-selected item should not clear the field
                if (newValue === undefined) return;
                onValueChange(newValue);
            }}
            options={options}
            disabled={disabled}
        >
            <div ref={fieldRef} tabIndex={-1} data-test={dataTest} className={cn('focus:outline-none', className)}>
                <ComboboxTrigger
                    placeholder={placeholder}
                    label={label}
                    error={error}
                    size={size}
                    className={cn('w-full', disabled && 'pointer-events-none')}
                />
            </div>
            <ComboboxContent shouldFilter={false}>
                <ComboboxSearch loading={loading} value={search} onValueChange={onSearchChange} />
                <ComboboxList>
                    <ComboboxEmpty>{t('errorMessages.common.noResults')}</ComboboxEmpty>
                    <ComboboxOptions<ComboboxOption>>
                        {option => (
                            <ComboboxItemWithDescription
                                key={option.value}
                                value={option.value}
                                label={option.label}
                                description={option.description}
                            />
                        )}
                    </ComboboxOptions>
                </ComboboxList>
            </ComboboxContent>
        </Combobox>
    );
}
